let ctx: AudioContext | null = null
let enabled = false

const SOUND_KEY = 'glass-office.sound'
function readSoundPref(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(SOUND_KEY) === '1'
  } catch {
    return false
  }
}
// User-facing on/off (separate from `enabled`, which is the autoplay arming).
// Off by default — sound is opt-in via the HUD toggle.
let on = readSoundPref()

export function isSfxOn(): boolean {
  return on
}

export function setSfxOn(value: boolean): void {
  on = value
  try {
    localStorage.setItem(SOUND_KEY, value ? '1' : '0')
  } catch {
    // ignore storage errors
  }
  // The toggle click is itself a user gesture, so arm/resume the context here.
  if (value) enableSfx()
}

function audioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null
  if (!ctx) {
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext
    if (!Ctor) return null
    ctx = new Ctor()
  }
  return ctx
}

/** Call from a user gesture to satisfy autoplay policy, then sounds are armed. */
export function enableSfx(): void {
  const context = audioContext()
  if (context && context.state === 'suspended') void context.resume()
  enabled = true
}

type Sound = 'message' | 'task' | 'reaction' | 'open' | 'close'
const FREQ: Record<Sound, number> = {
  message: 660,
  task: 880,
  reaction: 990,
  open: 520,
  close: 300,
}

/** A short, soft blip. No-op unless armed AND the user has sound on.
 *  'open' is a rising paper pop, 'close' a falling thunk (window juice). */
export function playSfx(sound: Sound): void {
  if (!enabled || !on) return
  const context = audioContext()
  if (!context) return
  const osc = context.createOscillator()
  const gain = context.createGain()
  osc.type = sound === 'open' ? 'triangle' : 'sine'
  osc.connect(gain)
  gain.connect(context.destination)
  const now = context.currentTime
  osc.frequency.setValueAtTime(FREQ[sound], now)
  if (sound === 'open') {
    osc.frequency.exponentialRampToValueAtTime(760, now + 0.09)
  } else if (sound === 'close') {
    osc.frequency.exponentialRampToValueAtTime(170, now + 0.11)
  }
  const peak = sound === 'open' || sound === 'close' ? 0.04 : 0.05
  const tail = sound === 'close' ? 0.14 : 0.18
  gain.gain.setValueAtTime(0.0001, now)
  gain.gain.exponentialRampToValueAtTime(peak, now + 0.01)
  gain.gain.exponentialRampToValueAtTime(0.0001, now + tail)
  osc.start(now)
  osc.stop(now + tail + 0.02)
}
